import * as React from 'react';
import {identity} from '@packages/utilities/fp/utils';
import {ConditionalRuleIcon} from '@packages/systems/spring';
import {styledDiv} from '@packages/utilities/styled';
import {
  Result,
  Plugins,
  EnvTypes,
  TypeImplementation,
  Types,
} from '@packages/systems/wfdl';
import {
  EHole,
  ECase,
  TTypeConstructor,
  TTypeApplication,
  TText,
  TNumber,
  unionOf,
  unionTypeName,
} from '@packages/systems/wfdl-ast';
import {
  type Qualified,
  qualify,
  typeName,
  pluginName,
  iconName,
  namesEqual,
} from '@packages/systems/wfdl-Qualified';
import {
  tyfun,
  forall,
  a,
  $,
  _$,
  call,
  call1,
  func,
} from '@packages/utilities/wfdl-helpers';
import {
  TPredicate,
  runPredicate,
  toBooleanTag,
  TBoolean,
} from '@packages/systems/plugins-utils/Boolean';
import {TOption} from '@packages/systems/plugins/Option';
import {TEmail} from '@packages/systems/plugins/Email';
import {TLink} from '@packages/systems/plugins/Link';
import {TVideoLink} from '@packages/systems/plugins/Video';
import {TColor} from '@packages/systems/plugins/Color';
import {LOGIC_BLOCK_CATEGORIES} from '@packages/systems/flows/shared';
import {PredicatedBoolean} from './PredicatedBoolean';

// Import of 'ExpressionEditor` here is from utils in order to avoid server-side runtime errors related to usage
// of browser APIs in the base `ExpressionEditor` component, exported from `@packages/systems/ExpressionEditor`
import {ExpressionEditor} from '@packages/systems/ExpressionEditor-utils';

import type {Type} from '@packages/systems/wfdl';

import {getAnn} from '@packages/utilities/contextual-expression';

const conditionalPluginName = pluginName('Conditional');
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const name = <nameType extends unknown>(n: nameType): Qualified<nameType> =>
  qualify(conditionalPluginName, n);

const tyOperand = TTypeConstructor(name(typeName('Operand')));

const tyConditionalUnion = TTypeConstructor(name(typeName('ConditionalUnion')));

export const TOperand = (arg: Type) =>
  TTypeApplication({
    con: tyOperand,
    arg,
  });

export const TConditionalUnion = (arg: Type) =>
  TTypeApplication({
    con: tyConditionalUnion,
    arg,
  });

const TEqualsInput = (arg: Type) =>
  TTypeApplication({
    con: TTypeConstructor(name(typeName('EqualsInput'))),
    arg,
  });

const Operand = call1(_$('Conditional', 'Operand'));
const RunOperand = call1(_$('Conditional', 'Run Operand'));

const ConditionalUnion = call1(_$('Conditional', 'ConditionalUnion'));

const operandExpr = _$('Conditional', 'apply');

const operand = (ty: Type) => ({
  envType: {
    dataType: tyfun([ty], TOperand(ty)),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  expression: operandExpr,
});

const operandOptionExpr = _$('Conditional', 'apply on Some');

const operandOption = (ty: Type) => ({
  envType: {
    dataType: tyfun(
      [
        TTypeApplication({
          con: TTypeConstructor(
            qualify(pluginName('Option'), typeName('WithFallbackLabel'))
          ),
          arg: TBoolean,
        }),
        TOption(ty),
      ],
      TOperand(ty)
    ),
    exported: 'visible',
    implicit: 'prefilledArgs',
  },
  expression: operandOptionExpr,
});

const FlowIconWrapper = styledDiv(({theme: {colors, gutters}}) => ({
  gridRow: '1 / 3',
  width: gutters.x12 * 3,
  height: gutters.x12 * 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${colors.border}`,
  borderRadius: '4px',
  color: colors.white,
  backgroundColor: colors.foregroundDimmer,
}));

const ConditionalRuleWrappedIcon = () => (
  <FlowIconWrapper>
    <ConditionalRuleIcon size={20} />
  </FlowIconWrapper>
);

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    IfElseMedium: ConditionalRuleWrappedIcon,
    IfElseSmall: ConditionalRuleWrappedIcon,
  },

  dataTypes: {
    PredicatedBoolean,
    /**
     * A nominally unique type that under the hood is a function used as the
     * first argument passed to `Apply predicate`.
     *
     * Using a nominally unique type for predicates prevents them from popping
     * up in unexpected places. This is because the type-system doesn’t see them
     * as functions and so it does not try to show calls to these functions in
     * binding menus.
     *
     * This type is accompanied by 2 functions defined further below:
     * - `Operand` which constructs an `Operand` value out of a function.
     * - `Run Operand` which given an `Operand` value returns back the
     *   original function.
     */
    Operand: TypeImplementation.Custom({
      params: ['a'],
      parseValue: (_) =>
        Result.Error(
          'Cannot use Literal expressions to create Operand values.'
        ),
      render: () => null,
    }),

    ConditionalUnion: TypeImplementation.Custom({
      params: ['a'],
      parseValue: (_) =>
        Result.Error(
          'Cannot use Literal expressions to create ConditionalUnion values.'
        ),
      render: () => null,
    }),

    // Custom Type Alias created to modify rendered UI for Conditional Equals operator
    EqualsInput: TypeImplementation.Alias({
      params: ['a'],
      type: a,
      system: {
        bindUIVariant: 'custom',
      },
      render: ({expr, dispatch}) => {
        const {type} = getAnn(expr);
        const tyApp = type ? Types.deconstructTypeApplication(type) : null;

        // When the expression is a union, but receives a single argument of an
        // unknown type, return placeholder text instead of an `ExpressionEditor`.
        // This prevents an empty `ExpressionEditor` with binding dot from being
        // rendered before a field has been selected for the Conditional Equals operator.
        if (
          tyApp &&
          namesEqual(tyApp.con, unionTypeName) &&
          tyApp.args.length === 1 &&
          // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
          Types.isUnknownType(tyApp.args[0])
        ) {
          return 'Please select a field above';
        }

        return <ExpressionEditor expression={expr} dispatch={dispatch} />;
      },
    }),
  },

  values: {
    Operand: {
      envType: {
        dataType: forall(
          ['a'],
          tyfun([tyfun([TPredicate(a)], TBoolean)], TOperand(a))
        ),
        exported: 'no',
        implicit: 'holeArg',
      },
      value: identity,
    },

    'Run Operand': {
      envType: {
        dataType: forall(['a'], tyfun([TOperand(a), TPredicate(a)], TBoolean)),
        exported: 'no',
      },
      value: identity,
    },

    apply: {
      envType: {
        dataType: forall(['a'], tyfun([a], TOperand(a))),
        exported: 'no',
      },
      expression: func(
        ['x'],
        Operand(
          func(['predicate'], call(runPredicate($('predicate')), [$('x')]))
        )
      ),
    },

    'apply on Some': {
      envType: {
        dataType: forall(['a'], tyfun([TBoolean, TOption(a)], TOperand(a))),
        exported: 'no',
      },
      expression: func(
        ['fallback', 'optional value'],
        Operand(
          func(
            ['predicate'],
            ECase({
              value: $('optional value'),
              cases: {
                None: func(['_'], $('fallback')),
                Some: runPredicate($('predicate')),
              },
            })
          )
        )
      ),
    },

    ConditionalUnion: {
      envType: {
        dataType: forall(['a'], tyfun([a], TConditionalUnion(a))),
        exported: 'no',
      },
      value: identity,
    },

    // Value which creates expression representing call to predicate on a non-optional `ConditionalUnion`
    'Apply on ConditionalUnion': {
      envType: {
        dataType: forall(['a'], tyfun([a], TOperand(TConditionalUnion(a)))),
        exported: 'hidden',
      },
      expression: func(
        ['x'],
        Operand(
          func(
            ['predicate'],
            call(runPredicate($('predicate')), [ConditionalUnion($('x'))])
          )
        )
      ),
    },

    // Value which creates expression representing call to predicate on an optional `ConditionalUnion`
    'Apply on Some ConditionalUnion': {
      envType: {
        dataType: forall(
          ['a'],
          tyfun([TBoolean, TOption(a)], TOperand(TConditionalUnion(a)))
        ),
        exported: 'hidden',
      },
      expression: func(
        ['fallback', 'optional value'],
        Operand(
          func(
            ['predicate'],
            ECase({
              value: $('optional value'),
              cases: {
                None: func(['_'], $('fallback')),
                Some: func(
                  ['x'],
                  call(runPredicate($('predicate')), [ConditionalUnion($('x'))])
                ),
              },
            })
          )
        )
      ),
    },

    'Apply predicate': {
      envType: {
        dataType: forall(
          ['a'],
          tyfun(
            [TOperand(a), TPredicate(a)],
            TTypeConstructor(name(typeName('PredicatedBoolean')))
          )
        ),
        exported: 'hidden',
      },
      expression: func(
        ['operand', 'predicate'],
        call(RunOperand($('operand')), [$('predicate')])
      ),
    },

    'Text Conditional': operand(TText),
    'Number Conditional': operand(TNumber),
    'Boolean Conditional': operand(TBoolean),
    'Email Conditional': operand(TEmail),
    'Link Conditional': operand(TLink),
    'Video Link Conditional': operand(TVideoLink),
    'Color Conditional': operand(TColor),

    'Option Text Conditional': operandOption(TText),
    'Option Number Conditional': operandOption(TNumber),
    'Option Boolean Conditional': operandOption(TBoolean),
    'Option Email Conditional': operandOption(TEmail),
    'Option Link Conditional': operandOption(TLink),
    'Option Video Link Conditional': operandOption(TVideoLink),
    'Option Color Conditional': operandOption(TColor),

    // Creates a `Conditional Equals` function used as a predicate
    // by comparing two unions by the value of `tag`.
    Equals: {
      envType: {
        dataType: forall(
          ['a'],
          tyfun(
            [TEqualsInput(unionOf(a))],
            TPredicate(TConditionalUnion(unionOf(a)))
          )
        ),
        exported: 'visible',
      },
      // Use of `toBooleanTag` here is required as the resulting
      // value will be evaluated as a TBoolean, not a JS boolean.
      // @ts-expect-error - TS7006 - Parameter 'x' implicitly has an 'any' type.
      value: (x) => (y: any) => toBooleanTag(x.tag === y.tag),
    },
  } as {
    [key: string]: EnvTypes.TypedValue;
  },

  valuePresets: {
    ifElseFactory: {
      factory: ECase({
        value: call(_$('Conditional', 'Apply predicate'), [EHole(), EHole()]),
        cases: {
          True: func(['True value'], _$('Effect', 'Done')),
          False: func(['False value'], _$('Effect', 'Done')),
        },
      }),
      label: 'Conditional rule',
      icon: {
        medium: name(iconName('IfElseMedium')),
        small: name(iconName('IfElseSmall')),
      },
      category: LOGIC_BLOCK_CATEGORIES.Utilities,
    },
  },
});
